/**
 * Assistant API client and run streaming.
 *
 * Streaming is POST-then-EventSource: EventSource cannot carry a request
 * body, but in exchange it reconnects on its own and replays through
 * Last-Event-ID, so a reload or a phone going through a tunnel resumes the
 * run instead of orphaning it.
 */
import type { AgentEvent, ChatMessageDto, ChatScopeKind, ChatThreadDto, ModelOption } from "@hydra-notes/shared";

export type { AgentEvent, ChatMessageDto, ChatThreadDto };

export interface ChatScope {
  kind: ChatScopeKind;
  noteId: string | null;
  /** Shared folder id when the conversation targets one; absent = personal. */
  spaceId?: string;
}

// personal keys keep their old shape so existing state stays addressable
export const scopeKey = (s: ChatScope): string =>
  s.kind === "note"
    ? s.spaceId
      ? `note:${s.spaceId}:${s.noteId}`
      : `note:${s.noteId}`
    : s.spaceId
      ? `workspace:${s.spaceId}`
      : "workspace";

async function json<T>(url: string, init?: RequestInit): Promise<T> {
  const res = await fetch(url, { credentials: "same-origin", ...init });
  if (!res.ok) {
    const body = (await res.json().catch(() => ({}))) as { error?: string };
    throw new Error(body.error ?? `request failed (${res.status})`);
  }
  return (await res.json()) as T;
}

export function listThreads(scope: ChatScope, archived = false): Promise<{ threads: ChatThreadDto[] }> {
  const params = new URLSearchParams({ scope: scope.kind });
  if (scope.kind === "note" && scope.noteId) params.set("noteId", scope.noteId);
  if (scope.spaceId) params.set("spaceId", scope.spaceId);
  if (archived) params.set("archived", "1");
  return json(`/api/agent/threads?${params.toString()}`);
}

export function createThread(scope: ChatScope): Promise<{ thread: ChatThreadDto }> {
  return json("/api/agent/threads", {
    method: "POST",
    headers: { "content-type": "application/json" },
    body: JSON.stringify({ scope: scope.kind, noteId: scope.noteId, ...(scope.spaceId ? { spaceId: scope.spaceId } : {}) }),
  });
}

export function patchThread(id: string, patch: { title?: string; archived?: boolean }): Promise<{ ok: true }> {
  return json(`/api/agent/threads/${id}`, {
    method: "PATCH",
    headers: { "content-type": "application/json" },
    body: JSON.stringify(patch),
  });
}

export function deleteThread(id: string): Promise<{ ok: true }> {
  return json(`/api/agent/threads/${id}`, { method: "DELETE" });
}

export function listMessages(threadId: string): Promise<{ messages: ChatMessageDto[] }> {
  return json(`/api/agent/threads/${threadId}/messages`);
}

export function listModels(): Promise<{ models: ModelOption[]; defaultModel: string }> {
  return json("/api/agent/models");
}

export function startRun(threadId: string, text: string, model?: string): Promise<{ runId: string }> {
  return json(`/api/agent/threads/${threadId}/runs`, {
    method: "POST",
    headers: { "content-type": "application/json" },
    body: JSON.stringify({ text, ...(model ? { model } : {}) }),
  });
}

export function cancelRun(runId: string): Promise<unknown> {
  return fetch(`/api/agent/runs/${runId}/cancel`, { method: "POST", credentials: "same-origin" }).catch(() => null);
}

/**
 * Stream a run. Returns a stop function. The browser handles reconnect, so
 * this only closes on a terminal event or when the caller stops it.
 */
export function streamRun(
  runId: string,
  onEvent: (e: AgentEvent) => void,
  onClose: (reason: "finished" | "error") => void,
): () => void {
  const source = new EventSource(`/api/agent/runs/${runId}/stream`);
  let closed = false;
  const shut = (reason: "finished" | "error") => {
    if (closed) return;
    closed = true;
    source.close();
    onClose(reason);
  };
  source.onmessage = (ev: MessageEvent<string>) => {
    let parsed: AgentEvent;
    try {
      parsed = JSON.parse(ev.data) as AgentEvent;
    } catch {
      return;
    }
    onEvent(parsed);
    if (parsed.t === "run.finished") shut("finished");
    if (parsed.t === "error") shut("error");
  };
  source.onerror = () => {
    // EventSource retries by itself; only give up once it has actually closed
    if (source.readyState === EventSource.CLOSED) shut("error");
  };
  return () => shut("finished");
}
